/* Shopify owns discount eligibility, amounts, and combination rules. */
(() => {
  if (customElements.get('cart-coupons')) return;
  let busy = false;
  let knownCart;
  let initialCartRequest;
  let revision = 0;

  const key = (code) => code.trim().toLowerCase();
  function discountCodes(cart) {
    if (Array.isArray(cart.discount_codes)) return cart.discount_codes;
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.discount_applications || []),
      ...(cart.items || []).flatMap((item) =>
        (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application)
      ),
    ];
    return applications
      .filter((application) => application?.type === 'discount_code')
      .map((application) => ({ code: application.title, applicable: true }));
  }

  function sectionsToRender() {
    const sections = [];
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const element = document.getElementById(id);
      if (element) sections.push({ section: element.dataset.id, target: `#${id} .js-contents` });
    }
    if (document.getElementById('CartDrawer')) {
      sections.push({ section: 'cart-drawer', target: '#CartDrawer .drawer__inner' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      sections.push({ section: 'cart-icon-bubble', target: '#cart-icon-bubble', source: '.shopify-section' });
    }
    return sections;
  }

  class CartCoupons extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      if (knownCart) this.renderApplied(knownCart);
      else {
        if (!initialCartRequest) {
          const initialRevision = revision;
          initialCartRequest = fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
            .then((response) => {
              if (!response.ok) throw new Error('Cart unavailable');
              return response.json();
            })
            .then((cart) => { if (revision === initialRevision) rememberCart(cart); })
            .catch(() => { initialCartRequest = undefined; });
        }
      }
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-coupon-remove')) {
          this.update(button.dataset.couponRemove, true);
        } else if (button.hasAttribute('data-coupon-code')) {
          this.querySelector('input').value = button.dataset.couponCode;
          this.update(button.dataset.couponCode);
        } else if (button.hasAttribute('data-coupon-submit')) {
          this.update(this.querySelector('input').value);
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('input') && !event.isComposing) {
          event.preventDefault();
          this.update(event.target.value);
        }
      });
      this.addEventListener('input', () => this.querySelector('input').removeAttribute('aria-invalid'));
    }

    renderApplied(cart) {
      const list = this.querySelector('.cart-coupons__applied');
      if (!list) return;
      const codes = new Map(discountCodes(cart)
        .filter((entry) => entry.applicable !== false)
        .map((entry) => [key(entry.code), entry.code]));
      list.replaceChildren();
      codes.forEach((code) => {
        const item = document.createElement('li');
        const label = document.createElement('span');
        label.textContent = code;
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.couponRemove = code;
        button.textContent = this.dataset.removeLabel;
        button.setAttribute('aria-label', `${this.dataset.removeLabel} ${code}`);
        item.append(label, button);
        list.append(item);
      });
    }

    message(text, error = false) {
      const status = this.querySelector('[role="status"]');
      status.textContent = text;
      status.dataset.error = String(error);
      this.querySelector('input').setAttribute('aria-invalid', String(error));
    }

    async update(value, remove = false) {
      if (busy) return;
      const code = value.trim();
      if (!code || code.includes(',')) {
        this.message(this.dataset.empty, true);
        this.querySelector('input').focus();
        return;
      }

      busy = true;
      revision++;
      const id = this.id;
      const offersOpen = this.querySelector('details')?.open;
      const disabled = [];
      document.querySelectorAll('cart-coupons').forEach((picker) => picker.setAttribute('aria-busy', 'true'));
      // Prevent checkout or quantity changes while the discount update is in flight.
      document.querySelectorAll('cart-coupons button, cart-coupons input, [name="checkout"], cart-items input[name="updates[]"], cart-drawer-items input[name="updates[]"], .quantity__button').forEach((control) => {
        if (!control.disabled) {
          disabled.push(control);
          control.disabled = true;
        }
      });
      const sections = sectionsToRender();
      let updated = false;
      try {
        const cartResponse = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('Cart unavailable');
        const current = await cartResponse.json();
        // Keep other codes, including shipping codes that have no cart allocation.
        const codes = new Map(discountCodes(current).map((entry) => [key(entry.code), entry.code]));
        if (remove) codes.delete(key(code));
        else codes.set(key(code), code);

        const response = await fetch(routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            discount: [...codes.values()].join(','),
            sections: [...new Set(sections.map((section) => section.section))],
            sections_url: window.location.pathname,
          }),
        });
        if (!response.ok) throw new Error('Discount update failed');
        const cart = await response.json();
        if (cart.errors) throw new Error('Discount update failed');
        updated = true;

        // Parse every fragment before mutating the page. Shopify can return null
        // sections even when the cart mutation succeeded; reload to show reality.
        const replacements = sections.map((section) => {
          const html = cart.sections?.[section.section];
          const source = html && new DOMParser().parseFromString(html, 'text/html').querySelector(section.source || section.target);
          const target = document.querySelector(section.target);
          if (!source || !target) throw new Error('Section unavailable');
          return { target, html: source.innerHTML };
        });
        replacements.forEach(({ target, html }) => { target.innerHTML = html; });
        rememberCart(cart);
        window.MsgCartUI?.syncBadgeFromResponse(cart);

        const picker = document.getElementById(id);
        if (picker) {
          const offerList = picker.querySelector('details');
          if (offerList) offerList.open = offersOpen;
          const resultCodes = discountCodes(cart);
          const applied = resultCodes.some((entry) => key(entry.code) === key(code) && entry.applicable !== false);
          if (remove && resultCodes.some((entry) => key(entry.code) === key(code))) throw new Error('Coupon still present');
          picker.querySelector('input').value = remove || applied ? '' : code;
          picker.message(remove ? picker.dataset.removed : applied ? picker.dataset.success : picker.dataset.invalid, !remove && !applied);
        }
        // CartItems ignores its own source; all visible cart sections are already refreshed.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
        }
      } catch (error) {
        const picker = document.getElementById(id) || this;
        picker.message(picker.dataset.error, true);
        // Never leave a stale total after a confirmed update and failed render.
        if (updated) window.location.reload();
      } finally {
        busy = false;
        disabled.forEach((control) => { if (control.isConnected) control.disabled = false; });
        document.querySelectorAll('cart-coupons').forEach((picker) => picker.removeAttribute('aria-busy'));
        document.getElementById(id)?.querySelector('input')?.focus({ preventScroll: true });
      }
    }
  }
  function rememberCart(cart) {
    knownCart = cart;
    document.querySelectorAll('cart-coupons').forEach((picker) => picker.renderApplied(cart));
  }
  customElements.define('cart-coupons', CartCoupons);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.cartData) {
        revision++;
        rememberCart(event.cartData);
      }
    });
  }
})();
